import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { X, Calendar, Info, ChevronRight, MapPin, Clock, Youtube, FileText, UserPlus, UsersRound, Star, Pin, Trophy, Award } from 'lucide-react';
import { API, BACKEND_URL, getLogoUrl, GOOGLE_FORMS } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import BracketMatch from '../components/BracketMatch';

const HomePage = () => {
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [completedGames, setCompletedGames] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState('roster');
  const [announcements, setAnnouncements] = useState([]);
  const [mvp, setMvp] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [playoffGames, setPlayoffGames] = useState([]);
  const [seasonFacts, setSeasonFacts] = useState({
    teams_count: '-',
    players_count: '-',
    games_count: '-',
    champion: '-'
  });
  const { t, language } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/teams?season=2`).then(res => {
      setTeams(res.data);
    }).catch(console.error);
    axios.get(`${API}/games?status=scheduled&season=2`).then(res => setGames(res.data.slice(0, 4))).catch(console.error);
    axios.get(`${API}/games?status=completed&season=2`).then(res => setCompletedGames(res.data)).catch(console.error);
    axios.get(`${API}/settings/season2`).then(res => {
      setSeasonFacts(prev => ({...prev, 
        teams_count: res.data.teams_count || '0',
        players_count: res.data.players_count || '0', 
        games_count: res.data.games_count || '0',
        champion: res.data.champion || '-'
      }));
    }).catch(console.error);
    axios.get(`${API}/announcements`).then(res => {
      // Sort by most recent first (ignore pinned for homepage)
      const sorted = [...res.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAnnouncements(sorted.slice(0, 3));
    }).catch(console.error);
    axios.get(`${API}/mvp?season=2`).then(res => setMvp(res.data)).catch(console.error);
    axios.get(`${API}/settings/season`).then(res => setYoutubeUrl(res.data.youtube_url || '')).catch(console.error);
    axios.get(`${API}/sponsors`).then(res => setSponsors(res.data)).catch(console.error);
  }, []);

  const statColumns = [
    { key: 'games', label: language === 'gr' ? 'ΑΓ' : 'GP', title: language === 'gr' ? 'Αγώνες' : 'Games' },
    { key: 'points', label: language === 'gr' ? 'ΠΟΝ' : 'PTS', title: language === 'gr' ? 'Πόντοι' : 'Points' },
    { key: 'free_throws', label: language === 'gr' ? 'Β' : 'FT', title: language === 'gr' ? 'Εύστοχες Βολές' : 'Free Throws Made' },
    { key: 'two_pointers', label: '2Π', title: language === 'gr' ? 'Εύστοχα 2ποντα' : '2-Pointers Made' },
    { key: 'three_pointers', label: '3Π', title: language === 'gr' ? 'Εύστοχα 3ποντα' : '3-Pointers Made' },
    { key: 'def_rebounds', label: language === 'gr' ? 'ΑΡ' : 'DR', title: language === 'gr' ? 'Αμυντικά Ριμπάουντ' : 'Def Rebounds' },
    { key: 'off_rebounds', label: language === 'gr' ? 'ΕΡ' : 'OR', title: language === 'gr' ? 'Επιθετικά Ριμπάουντ' : 'Off Rebounds' },
    { key: 'total_rebounds', label: language === 'gr' ? 'ΣΡ' : 'REB', title: language === 'gr' ? 'Σύνολο Ριμπάουντ' : 'Total Rebounds' },
    { key: 'steals', label: language === 'gr' ? 'ΚΛ' : 'STL', title: language === 'gr' ? 'Κλεψίματα' : 'Steals' },
    { key: 'turnovers', label: language === 'gr' ? 'ΛΑ' : 'TO', title: language === 'gr' ? 'Λάθη' : 'Turnovers' },
  ];

  const getTeamGames = (teamId) => {
    return completedGames.filter(g => g.home_team_id === teamId || g.away_team_id === teamId);
  };

  const handlePlayerRegistration = () => {
    if (GOOGLE_FORMS.playerRegistration) {
      window.open(GOOGLE_FORMS.playerRegistration, '_blank');
    } else {
      alert('Registration form coming soon!');
    }
  };

  const handleTeamRegistration = () => {
    if (GOOGLE_FORMS.teamRegistration) {
      window.open(GOOGLE_FORMS.teamRegistration, '_blank');
    } else {
      alert('Registration form coming soon!');
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section - Dynamic with cinematic animations */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background with Ken Burns effect */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/fe701d07-2623-40a5-bfd5-5f21ea99275b/images/b5b9cbe824bc016feaa28d3d265b0843dad23feeb5dcb8e3382c3b79553db56a.png" 
            alt="WBLA Basketball" 
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.55) saturate(1.2)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A5F]/85 via-[#1E3A5F]/50 to-transparent"></div>
        </div>
        
        {/* Animated elements - subtle */}
        <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none">
          {/* Orbiting circles */}
          <div className="absolute top-1/2 right-1/4 w-0 h-0">
            <div className="hero-orbit-1">
              <div className="w-2 h-2 bg-[#F97316] rounded-full hero-glow"></div>
            </div>
          </div>
          
          {/* Floating particles */}
          <div className="absolute top-[20%] right-[15%] w-1.5 h-1.5 bg-[#F97316] rounded-full" style={{ animation: 'glow 3s ease-in-out infinite' }}></div>
          <div className="absolute top-[60%] right-[25%] w-1 h-1 bg-white/50 rounded-full" style={{ animation: 'glow 4s ease-in-out infinite 1s' }}></div>
          <div className="absolute top-[75%] right-[40%] w-1.5 h-1.5 bg-[#F97316]/40 rounded-full" style={{ animation: 'glow 3.5s ease-in-out infinite 0.5s' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <div className="space-y-6">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium"
                style={{ animation: 'fadeInUp 0.6s ease-out' }}
              >
                <span className="w-2 h-2 bg-[#F97316] rounded-full animate-pulse"></span>
                {t('season')} 2 • 2026
              </div>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-white leading-tight"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.1s backwards' }}
              >
                {t('heroTitle')}
              </h1>
              
              <p 
                className="text-lg sm:text-xl text-slate-300 max-w-xl"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.2s backwards' }}
              >
                {t('heroSubtitle')}
              </p>
              
              <div 
                className="flex flex-wrap gap-4 pt-4"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.3s backwards' }}
              >
                <Link 
                  to="/season2/schedule" 
                  className="group inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-[#F97316]/25 hover:shadow-[#F97316]/40 hover:-translate-y-0.5" 
                  data-testid="hero-schedule-btn"
                >
                  {t('viewSchedule')} 
                  <ChevronRight size={20} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link 
                  to="/teams" 
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:-translate-y-0.5" 
                  data-testid="hero-teams-btn"
                >
                  {t('meetTeams')}
                </Link>
              </div>
            </div>

            {/* Right side - Stats/Info cards */}
            <div 
              className="hidden lg:grid grid-cols-2 gap-4"
              style={{ animation: 'slideIn 0.8s ease-out 0.4s backwards' }}
            >
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 hover:border-[#F97316]/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#F97316]/10">
                <div className="text-4xl font-display font-bold text-[#F97316] mb-2">{seasonFacts.teams_count}</div>
                <div className="text-white/80 font-medium">{language === 'gr' ? 'Ομάδες' : 'Teams'}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 hover:border-[#F97316]/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#F97316]/10" style={{ animationDelay: '0.1s' }}>
                <div className="text-4xl font-display font-bold text-[#F97316] mb-2">{seasonFacts.players_count}</div>
                <div className="text-white/80 font-medium">{language === 'gr' ? 'Παίκτριες' : 'Players'}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 hover:border-[#F97316]/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#F97316]/10" style={{ animationDelay: '0.2s' }}>
                <div className="text-4xl font-display font-bold text-[#F97316] mb-2">{seasonFacts.games_count}</div>
                <div className="text-white/80 font-medium">{language === 'gr' ? 'Αγώνες' : 'Games'}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 hover:border-[#F97316]/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#F97316]/10" style={{ animationDelay: '0.3s' }}>
                <div className="text-4xl font-display font-bold text-[#F97316] mb-2">{seasonFacts.champion || '-'}</div>
                <div className="text-white/80 font-medium">{language === 'gr' ? 'Πρωταθλητής' : 'Champion'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-[2]"></div>
      </section>

      {/* Registration Section */}
      <section className="bg-gradient-to-r from-[#F97316] to-[#EA580C] py-8" data-testid="registration-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <h3 className="text-white font-display font-bold text-xl md:text-2xl">{t('joinUs')}</h3>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={handlePlayerRegistration}
                className="flex items-center gap-2 bg-white text-[#F97316] px-6 py-3 rounded-lg font-semibold hover:bg-slate-100 transition shadow-lg"
                data-testid="register-player-btn"
              >
                <UserPlus size={20} />
                {t('registerPlayer')}
              </button>
              <button 
                onClick={handleTeamRegistration}
                className="flex items-center gap-2 bg-[#1E3A5F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2D5A87] transition shadow-lg"
                data-testid="register-team-btn"
              >
                <UsersRound size={20} />
                {t('registerTeam')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Games */}
      <section className="max-w-7xl mx-auto px-4 py-16" data-testid="upcoming-games">
        <div className="flex justify-between items-center mb-8">
          <h2 className="section-title">{t('upcomingGames')}</h2>
          <Link to="/season2/schedule" className="text-[#F97316] font-semibold hover:underline">{t('viewAll')}</Link>
        </div>
        {(() => {
          const getDayName = (dateStr) => { try { const d = new Date(dateStr + 'T00:00:00'); const days = language === 'gr' ? ['Κυριακή','Δευτέρα','Τρίτη','Τετάρτη','Πέμπτη','Παρασκευή','Σάββατο'] : ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']; return days[d.getDay()]; } catch { return ''; } };
          const fmtDate = (dateStr) => { try { const p = dateStr.split('-'); return `${p[2]}/${p[1]}/${p[0]}`; } catch { return dateStr; } };
          const sorted = [...games].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
          const renderCard = (game) => (
            <div key={game.id} className="card p-5 hover-lift" data-testid={`game-card-${game.id}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-slate-500 flex items-center"><Calendar size={16} className="mr-1.5" /> {getDayName(game.date)} {fmtDate(game.date)}</span>
                <span className="text-sm text-slate-500 flex items-center"><Clock size={16} className="mr-1.5" /> {game.time}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-center flex-1 flex flex-col items-center">
                  {game.home_team_id && <img src={getLogoUrl(game.home_team_id)} alt="" className="w-10 h-10 rounded-full object-cover mb-2" onError={e => { e.target.style.display='none'; }} />}
                  <p className="font-bold text-sm">{game.home_team_name}</p>
                  <span className="text-xs text-slate-400">{t('homeLabel')}</span>
                </div>
                <div className="px-3 py-1.5 bg-slate-100 rounded-lg font-display font-bold text-slate-500 text-sm">{t('vs')}</div>
                <div className="text-center flex-1 flex flex-col items-center">
                  {game.away_team_id && <img src={getLogoUrl(game.away_team_id)} alt="" className="w-10 h-10 rounded-full object-cover mb-2" onError={e => { e.target.style.display='none'; }} />}
                  <p className="font-bold text-sm">{game.away_team_name}</p>
                  <span className="text-xs text-slate-400">{t('awayLabel')}</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center text-xs text-slate-400">
                <MapPin size={14} className="mr-1.5" /> {game.venue}
              </div>
            </div>
          );
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {sorted.map(renderCard)}
            </div>
          );
        })()}
      </section>

      {/* Playoff Brackets */}
      {playoffGames.length > 0 && (() => {
        const top6 = [...playoffGames.filter(g => g.phase === 'Top 6 Playoffs')].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
        const semis = playoffGames.filter(g => g.phase?.includes('Ημιτελικά'));
        const smallFinal = playoffGames.filter(g => g.phase?.includes('Μικρός'));
        const grandFinal = playoffGames.filter(g => g.phase?.includes('Μεγάλος'));
        const placement = playoffGames.filter(g => g.phase === 'Αγώνες Κατάταξης Θέσεων 7–9');
        const placementFinal = playoffGames.filter(g => g.phase?.includes('Τελικός Κατάταξης'));
        return (
          <section className="max-w-7xl mx-auto px-4 py-10">
            <div className="card overflow-hidden mb-8" data-testid="home-playoff-bracket">
              <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white p-4">
                <h2 className="font-display font-bold text-xl flex items-center">
                  <Trophy className="mr-2" /> Top 6 Playoffs & Final 4
                </h2>
              </div>
              <div className="p-6 bg-slate-50">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">Top 6 Playoffs</h3>
                    {top6.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">{language === 'gr' ? 'Ημιτελικά' : 'Semifinals'}</h3>
                    {semis.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">{language === 'gr' ? 'Μικρός Τελικός' : 'Small Final'}</h3>
                    {smallFinal.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-[#F97316] uppercase tracking-wider text-center flex items-center justify-center gap-1">
                      <Award size={14} /> {language === 'gr' ? 'Μεγάλος Τελικός' : 'Grand Final'}
                    </h3>
                    {grandFinal.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                </div>
              </div>
            </div>
            <div className="card overflow-hidden" data-testid="home-placement-bracket">
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A87] text-white p-4">
                <h2 className="font-display font-bold text-xl flex items-center">
                  <Star className="mr-2" /> {language === 'gr' ? 'Κατάταξη Θέσεων 7–9' : 'Placement 7–9'}
                </h2>
              </div>
              <div className="p-6 bg-slate-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center max-w-2xl mx-auto">
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">{language === 'gr' ? 'Αγώνας 8ης vs 9ης' : '8th vs 9th'}</h3>
                    {placement.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-[#F97316] uppercase tracking-wider text-center flex items-center justify-center gap-1">
                      <Award size={14} /> {language === 'gr' ? 'Τελικός Κατάταξης' : 'Placement Final'}
                    </h3>
                    {placementFinal.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Teams Preview */}
      <section className="bg-slate-100 py-16" data-testid="teams-preview">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="section-title">{t('ourTeams')}</h2>
            <Link to="/teams" className="text-[#F97316] font-semibold hover:underline">{t('viewAllTeams')}</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {teams.map(team => (
              <div key={team.id} onClick={() => { setSelectedTeam(team); setActiveTab('roster'); }} className="card p-4 text-center hover-lift cursor-pointer" data-testid={`team-preview-${team.id}`}>
                {team.has_logo ? (
                  <img src={getLogoUrl(team.id)} alt={team.name} className="w-16 h-16 rounded-full mx-auto mb-3 object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: team.color }}>
                    {team.short_name}
                  </div>
                )}
                <h3 className="font-semibold text-[#1E3A5F]">{team.name}</h3>
                <p className="text-sm text-slate-500">{team.wins}W - {team.losses}L</p>
                {(team.top_scorer || team.top_rebounder) && (
                  <div className="mt-1 space-y-0.5" data-testid={`team-leaders-${team.id}`}>
                    {team.top_scorer && (
                      <p className="text-xs text-[#F97316] font-medium">
                        {language === 'gr' ? 'Πόντοι' : 'PTS'}: {team.top_scorer} ({team.top_scorer_points})
                      </p>
                    )}
                    {team.top_rebounder && (
                      <p className="text-xs text-[#1E3A5F] font-medium">
                        {language === 'gr' ? 'Ριμπ.' : 'REB'}: {team.top_rebounder} ({team.top_rebounder_rebounds})
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Detail Modal */}
      {selectedTeam && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTeam(null)} data-testid="home-team-modal">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 text-white" style={{ backgroundColor: selectedTeam.color }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {selectedTeam.has_logo && (
                    <img src={getLogoUrl(selectedTeam.id)} alt={selectedTeam.name} className="w-16 h-16 rounded-full object-cover border-2 border-white/30" />
                  )}
                  <div>
                    <h2 className="font-display font-bold text-2xl">{selectedTeam.name}</h2>
                    <p className="text-white/80">{selectedTeam.coach ? `${t('coach')}: ${selectedTeam.coach}` : ''}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedTeam(null)} className="text-white/80 hover:text-white" data-testid="home-modal-close"><X size={24} /></button>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="border-b border-slate-200">
              <div className="flex">
                <button 
                  onClick={() => setActiveTab('roster')}
                  className={`px-6 py-3 font-medium transition ${activeTab === 'roster' ? 'text-[#1E3A5F] border-b-2 border-[#F97316]' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="home-modal-tab-roster"
                >
                  {t('roster')}
                </button>
                <button 
                  onClick={() => setActiveTab('stats')}
                  className={`px-6 py-3 font-medium transition ${activeTab === 'stats' ? 'text-[#1E3A5F] border-b-2 border-[#F97316]' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="home-modal-tab-stats"
                >
                  {language === 'gr' ? 'Στατιστικά' : 'Statistics'}
                </button>
                <button 
                  onClick={() => setActiveTab('results')}
                  className={`px-6 py-3 font-medium transition ${activeTab === 'results' ? 'text-[#1E3A5F] border-b-2 border-[#F97316]' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="home-modal-tab-results"
                >
                  {t('results')}
                </button>
              </div>
            </div>
            
            {/* Tab Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Roster Tab */}
              {activeTab === 'roster' && (
                <div>
                  <h3 className="font-bold text-lg mb-4">{t('roster')} ({selectedTeam.players?.length || 0} {t('players')})</h3>
                  <div className="space-y-3">
                    {selectedTeam.players?.map(player => (
                      <div key={player.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`home-modal-player-${player.id}`}>
                        <div className="flex items-center gap-3">
                          <span className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold" style={{ color: selectedTeam.color }}>
                            #{player.number}
                          </span>
                          <div>
                            <p className="font-medium">{player.name}</p>
                            <p className="text-xs text-slate-500">{player.position} {player.height ? `• ${player.height}` : ''}</p>
                          </div>
                        </div>
                        {player.age && <span className="text-sm text-slate-500">{t('age')} {player.age}</span>}
                      </div>
                    ))}
                    {(!selectedTeam.players || selectedTeam.players.length === 0) && (
                      <p className="text-center text-slate-400 py-8">{language === 'gr' ? 'Δεν υπάρχουν παίκτριες' : 'No players'}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Stats Tab */}
              {activeTab === 'stats' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-3 font-semibold sticky left-0 bg-slate-100">#</th>
                        <th className="text-left p-3 font-semibold sticky left-10 bg-slate-100">{language === 'gr' ? 'Παίκτρια' : 'Player'}</th>
                        {statColumns.map(col => (
                          <th key={col.key} className="text-center p-3 font-semibold whitespace-nowrap" title={col.title}>
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTeam.players?.map((player, idx) => (
                        <tr key={player.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-3 font-medium sticky left-0" style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc' }}>{player.number}</td>
                          <td className="p-3 sticky left-10 whitespace-nowrap" style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc' }}>{player.name}</td>
                          {statColumns.map(col => (
                            <td key={col.key} className="text-center p-3 whitespace-nowrap">
                              {player.stats?.[col.key] ?? 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!selectedTeam.players || selectedTeam.players.length === 0) && (
                    <p className="text-center text-slate-400 py-8">{language === 'gr' ? 'Δεν υπάρχουν παίκτριες' : 'No players'}</p>
                  )}
                </div>
              )}
              
              {/* Results Tab */}
              {activeTab === 'results' && (
                <div>
                  {getTeamGames(selectedTeam.id).length > 0 ? (
                    <div className="space-y-3">
                      {getTeamGames(selectedTeam.id).map(game => (
                        <div key={game.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`home-modal-result-${game.id}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                              <Calendar size={14} /> {game.date}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`font-semibold ${game.home_team_id === selectedTeam.id ? 'text-[#1E3A5F]' : 'text-slate-600'}`}>
                                {game.home_team_name}
                              </span>
                              <span className="px-3 py-1 bg-white rounded font-bold text-sm">
                                {game.home_score} - {game.away_score}
                              </span>
                              <span className={`font-semibold ${game.away_team_id === selectedTeam.id ? 'text-[#1E3A5F]' : 'text-slate-600'}`}>
                                {game.away_team_name}
                              </span>
                            </div>
                          </div>
                          {game.video_url && (
                            <a href={game.video_url} target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-700">
                              <Youtube size={20} />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 py-8">
                      {language === 'gr' ? 'Δεν υπάρχουν αποτελέσματα ακόμα' : 'No results yet'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MVP Section */}
      {mvp?.has_mvp && (
        <section className="max-w-7xl mx-auto px-4 py-16" data-testid="mvp-section">
          <h2 className="section-title mb-8">{t('mvpTitle')}</h2>
          <div className="card p-0 overflow-hidden max-w-2xl mx-auto">
            <div className="p-6 text-white flex items-center gap-6" style={{ backgroundColor: mvp.team_color }}>
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold border-2 border-white/30">
                #{mvp.player_number}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Star size={20} className="text-yellow-300" />
                  <span className="text-sm text-white/80 uppercase tracking-wider font-semibold">MVP</span>
                </div>
                <h3 className="text-2xl font-display font-bold">{mvp.player_name}</h3>
                <p className="text-white/80 flex items-center gap-2">
                  {mvp.has_logo && <img src={getLogoUrl(mvp.team_id)} alt="" className="w-5 h-5 rounded-full" />}
                  {mvp.team_name}
                </p>
              </div>
            </div>
            <div className="p-6 grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-[#1E3A5F]">{mvp.stats?.points || 0}</p>
                <p className="text-xs text-slate-500">{language === 'gr' ? 'Πόντοι' : 'Points'}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1E3A5F]">{mvp.stats?.total_rebounds || 0}</p>
                <p className="text-xs text-slate-500">{language === 'gr' ? 'Ριμπάουντ' : 'Rebounds'}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#F97316]">{mvp.stats?.steals || 0}</p>
                <p className="text-xs text-slate-500">{language === 'gr' ? 'Κλεψίματα' : 'Steals'}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Latest News */}
      {announcements.length > 0 && (
        <section className="bg-slate-100 py-16" data-testid="latest-news">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center mb-8">
              <h2 className="section-title">{t('latestNews')}</h2>
              <Link to="/news" className="text-[#F97316] font-semibold hover:underline">{t('readMore')}</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {announcements.map(ann => (
                <div key={ann.id} className="card p-6" data-testid={`news-card-${ann.id}`}>
                  {ann.pinned && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#F97316] font-semibold mb-2">
                      <Pin size={12} /> {t('pinned')}
                    </span>
                  )}
                  <h3 className="font-display font-bold text-lg text-[#1E3A5F] mb-2">{ann.title}</h3>
                  <p className="text-slate-600 text-sm line-clamp-3 mb-3">{ann.content}</p>
                  {/* Preview first image attachment */}
                  {ann.attachments?.filter(a => a.file_type === 'image').length > 0 && (
                    <img src={`${BACKEND_URL}${ann.attachments.find(a => a.file_type === 'image').url}`} alt="" className="w-full h-auto max-h-96 rounded-lg object-contain bg-slate-50 mb-3" data-testid="home-announcement-image" />
                  )}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">{new Date(ann.created_at).toLocaleDateString('el-GR')}</p>
                    {ann.attachments?.length > 0 && (
                      <span className="text-xs text-slate-400 flex items-center gap-1"><FileText size={12} /> {ann.attachments.length}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-12" data-testid="sponsors-section">
          <h2 className="text-center text-lg font-semibold text-slate-400 mb-8 uppercase tracking-wider">
            {language === 'gr' ? 'Χορηγοί Διοργάνωσης' : 'Event Sponsors'}
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {sponsors.map(sponsor => {
              const sponsorUrl = sponsor.url && !sponsor.url.startsWith('http') ? `https://${sponsor.url}` : sponsor.url;
              return (
              <div key={sponsor.id} className="flex flex-col items-center gap-2" data-testid={`sponsor-${sponsor.id}`}>
                {sponsor.title && (
                  <span className="text-sm font-semibold text-[#1E3A5F] uppercase tracking-wider" data-testid={`sponsor-title-${sponsor.id}`}>{sponsor.title}</span>
                )}
                <a
                  href={sponsorUrl || '#'}
                  target={sponsorUrl ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                  title={sponsor.name}
                >
                  {sponsor.has_logo ? (
                    <img
                      src={`${BACKEND_URL}/api/sponsors/${sponsor.id}/logo`}
                      alt={sponsor.name}
                      className="max-h-36 md:max-h-60 w-auto object-contain"
                      onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='block'); }}
                    />
                  ) : null}
                  <span className={`text-xl font-bold text-slate-500 group-hover:text-[#1E3A5F] ${sponsor.has_logo ? 'hidden' : ''}`}>
                    {sponsor.name}
                  </span>
                </a>
              </div>
            )})}
          </div>
        </section>
      )}

      {/* Social Media Section */}
      <section className="max-w-7xl mx-auto px-4 py-12" data-testid="social-section">
        <div className="text-center">
          <h2 className="section-title mb-6">{language === 'gr' ? 'Ακολουθήστε μας' : 'Follow Us'}</h2>
          <div className="flex justify-center gap-4">
            <a href="https://www.facebook.com/share/1FkJdSWQAv/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition text-xl" data-testid="social-facebook">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://www.instagram.com/womensbasketleague_gr?igsh=MXFkNjY1eTRqMjBsaw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white flex items-center justify-center hover:opacity-80 transition" data-testid="social-instagram">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
            <a href={youtubeUrl || 'https://www.youtube.com'} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-full bg-[#FF0000] text-white flex items-center justify-center hover:opacity-80 transition" data-testid="social-youtube">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 py-16" data-testid="cta-section">
        <div className="gradient-primary rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">{t('joinCommunity')}</h2>
          <p className="text-slate-200 max-w-2xl mx-auto mb-8">{t('communityText')}</p>
          <Link to="/contact" className="btn-accent inline-block" data-testid="cta-contact-btn">{t('getInTouch')}</Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
